import { useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bookmark, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Page qui affiche le bookmarklet à glisser dans la barre de favoris.
 * Le bookmarklet, une fois cliqué sur n'importe quelle page web, ouvre
 * /quick-curate sur AITO.PF avec URL/titre/image préremplis.
 */
export default function BookmarkletPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const canAccess = !!user && (user.roles || []).some((r) => ["contributor", "contributor_trusted", "admin", "copf"].includes(r));

  // Code JS du bookmarklet — extrait les méta og: + envoie vers /quick-curate
  const bookmarkletCode = useMemo(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "https://aito.pf";
    // Minifié, encodé pour URL bookmarklet
    const js = `
javascript:(function(){
  var d=document,m=function(p){var e=d.querySelector('meta[property="og:'+p+'"]')||d.querySelector('meta[name="twitter:'+p+'"]')||d.querySelector('meta[name="'+p+'"]');return e?e.content:''};
  var t=m('title')||d.title||'';
  var img=m('image')||'';
  var desc=m('description')||m('description')||'';
  var src=(m('site_name')||location.hostname).replace(/^www\\./,'');
  var u='${base}/quick-curate?url='+encodeURIComponent(location.href)+'&title='+encodeURIComponent(t)+'&image='+encodeURIComponent(img)+'&description='+encodeURIComponent(desc)+'&source='+encodeURIComponent(src);
  window.open(u,'aito_curate','width=720,height=900,scrollbars=yes,resizable=yes');
})();`.replace(/\s+/g, " ").trim();
    return js;
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(bookmarkletCode);
      setCopied(true);
      toast.success("Code copié dans le presse-papiers.");
      setTimeout(() => setCopied(false), 3000);
    } catch { toast.error("Copie échouée — sélectionne et copie manuellement."); }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p>Connexion requise.</p>
        <Button onClick={() => navigate("/connexion?next=/bookmarklet")} className="mt-3">Se connecter</Button>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p>Cet outil est réservé aux contributeurs et administrateurs.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-2">
        <Bookmark className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Bookmarklet « Ajouter à AITO.PF »</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Un raccourci à glisser dans la barre des favoris de ton navigateur. Quand tu lis un article ailleurs, un seul clic ouvre AITO.PF avec le titre, l'image et l'URL pré-remplis.
      </p>

      <Card className="p-5">
        <h2 className="font-display font-semibold text-lg mb-3">Étape 1 — Affiche la barre des favoris</h2>
        <ul className="text-sm space-y-1 list-disc pl-5">
          <li><b>Chrome / Edge</b> : <code className="bg-muted px-1.5 py-0.5 rounded">Ctrl/Cmd + Shift + B</code></li>
          <li><b>Firefox</b> : Menu → Personnaliser → activer la barre des favoris</li>
          <li><b>Safari</b> : Affichage → Afficher la barre des favoris</li>
        </ul>
      </Card>

      <Card className="p-5 mt-4">
        <h2 className="font-display font-semibold text-lg mb-3">Étape 2 — Glisse ce lien dans ta barre des favoris</h2>
        <div className="p-4 rounded-lg border-2 border-dashed border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon)/0.05)] flex items-center justify-center">
          <a
            href={bookmarkletCode}
            // eslint-disable-next-line react/no-unknown-property
            onClick={(e) => { e.preventDefault(); toast.info("Glisse-le plutôt dans la barre des favoris ⬆️"); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(var(--aito-lagoon))] text-black rounded-md font-medium hover:scale-105 transition-transform cursor-grab"
            data-testid="bookmarklet-drag-link"
          >
            <Bookmark className="h-4 w-4" />Ajouter à AITO.PF
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Clique-glisse le bouton ci-dessus directement dans la barre des favoris. Le bookmarklet est créé automatiquement.</p>
      </Card>

      <Card className="p-5 mt-4">
        <h2 className="font-display font-semibold text-lg mb-3">Étape 3 (alternative) — Copie manuelle du code</h2>
        <p className="text-xs text-muted-foreground mb-2">Si le drag&drop ne fonctionne pas, copie le code ci-dessous, crée un nouveau favori et colle-le dans le champ "URL/Adresse" du favori.</p>
        <textarea readOnly value={bookmarkletCode} rows={4} className="w-full text-xs font-mono p-2 border rounded bg-muted" />
        <Button onClick={copyCode} className="mt-2" data-testid="bookmarklet-copy">
          {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
          {copied ? "Copié !" : "Copier le code"}
        </Button>
      </Card>

      <Card className="p-5 mt-4 bg-[hsl(var(--aito-gold)/0.05)]">
        <h2 className="font-display font-semibold text-lg mb-2">Comment l'utiliser</h2>
        <ol className="text-sm space-y-1 list-decimal pl-5">
          <li>Tu lis un article sportif intéressant sur Tahiti Infos, TNTV, etc.</li>
          <li>Tu cliques sur le favori « Ajouter à AITO.PF »</li>
          <li>Une fenêtre AITO.PF s'ouvre, déjà remplie avec titre, image et URL</li>
          <li>Tu choisis discipline + entité liée + tu coches la déclaration de droits</li>
          <li>Tu cliques « Envoyer pour validation » (ou « Publier directement » si tu es admin)</li>
        </ol>
      </Card>
    </div>
  );
}
